/**
 * Adapted from PlayCanvas Engine's framework/components/gsplat/component.js and system.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import { UnsupportedGraphicsCapabilityError } from '@/graphics/GraphicsErrors'
import { compileGSplatWorkBufferModifierForLanguage } from '@/plugins/gsplat/effects/workBufferModifier'
import type {
  GSplatGraphicsAdapter,
  GSplatTextureLeaf,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatFormat } from '@/plugins/gsplat/lod/GSplatFormat'
import type { GSplatStreams } from '@/plugins/gsplat/lod/GSplatStreams'
import type { Layer } from '@/plugins/gsplat/lod/Layer'
import type {
  LayerComposition,
  LayerCompositionChange,
} from '@/plugins/gsplat/lod/LayerComposition'
import {
  type GSplatParameter,
  GSplatPlacement,
  type GSplatPlacementResource,
} from '@/plugins/gsplat/unified/GSplatPlacement'
import { getLoggerManager } from '@/shared/logging'
import type { AssetId, Disposable } from '@/shared/types/core'
import {
  type CompiledGSplatWorkBufferModifier,
  GSPLAT_WORKBUFFER_UPDATE_ALWAYS,
  GSPLAT_WORKBUFFER_UPDATE_AUTO,
  GSPLAT_WORKBUFFER_UPDATE_ONCE,
  type GSplatShaderEffectLike,
  type GSplatWorkBufferModifier,
  type GSplatWorkBufferUpdateMode,
  type LODStats,
} from '@/shared/types/gsplat'
import type { GaussianSplatHandle, GaussianSplatLoadRequest } from './types/PluginTypes'

const DEFAULT_LAYER_ID = 0
const diagnosticLog = getLoggerManager().getLogger('gsplat:diagnostics')
const EMPTY_LOD_STATS: LODStats = Object.freeze({
  totalGroups: 0,
  visibleNodes: 0,
  loadedNodes: 0,
  cachedPoints: 0,
  pendingLoads: 0,
  activeLoads: 0,
  cachedFiles: 0,
})

let nextPickerId = 0

/** format-specific Resource 的独立引用租约。 */
export interface GSplatResourceLease extends Disposable {
  readonly resourceIdentity?: object
  /** 仅 GPU-backed Resource 声明；Octree metadata 等纯 CPU Resource 可省略。 */
  readonly graphicsAdapter?: GSplatGraphicsAdapter
  readonly numSplats: number
  readonly boundingBox: THREE.Box3 | null
  readonly aabb?: THREE.Box3 | null
  readonly object3D: THREE.Object3D | null
  readonly generation: number
  readonly format?: GSplatFormat | null
  /** Resource family used by exporter capability checks. */
  readonly resourceKind?: 'uncompressed' | 'compressed' | 'sog' | 'octree' | 'container' | 'unknown'
  /** Static data or an Octree/LOD resource. */
  readonly streaming?: 'static' | 'lod'
  /** Octree export semantics exposed by this lease. */
  readonly octreeSemantics?: 'none' | 'dataset' | 'active-set' | 'dataset-and-active-set'
  readonly streams?: Pick<GSplatStreams, 'textureDimensions'> | null
  /** format-specific CPU data；仅在 lease 生命周期内有效。 */
  readonly data?: unknown
  /** CPU centers 出口；仅在 lease 生命周期内有效。 */
  readonly centers?: Float32Array | null
  /** 当前 format-specific source textures 的 GPU 字节数；跨 View residency 只按 identity 计一次。 */
  readonly sourceGpuBytes?: number
  readonly parameters?: ReadonlyMap<string, unknown>
  readonly centersVersion?: number
  /** 创建 format-specific CPU iterator；调用方不得在 lease 释放后继续使用。 */
  createIterator?(): unknown | null
  getTexture?(name: string): GSplatTextureLeaf | null
  configureMaterialDefines?(defines: Map<string, unknown>): void
  markStreamsWritten?(streamNames: readonly string[]): void
  getStreamRevision?(name: string): number
  getCpuExportData(): unknown | null
  /** 只读 CPU picking 形状出口；radii 为已激活的实际半径，不得修改 Resource 数据。 */
  readSplatShape?(
    index: number,
    position: THREE.Vector3,
    rotation: THREE.Quaternion,
    radii: THREE.Vector3
  ): boolean
  /** clone/binding replacement 必须取得独立 lease，不能共享同一个 dispose token。 */
  acquire(): GSplatResourceLease
}

export type GSplatBindingResourceSource =
  | { readonly kind: 'asset'; readonly assetId: AssetId }
  | { readonly kind: 'direct' }

export interface GSplatBindingTransaction {
  /** stage 成功后的无失败状态交换。 */
  commit(): void
  /** finalize 前恢复 stage 前状态；必须幂等。 */
  rollback(): void
}

export type GSplatBindingInvalidationReason =
  | 'membership'
  | 'visibility'
  | 'parameter'
  | 'work-buffer-update'
  | 'resource-replaced'
  | 'processor'

/** 只触达引用目标 Placement 的 Manager/Director。 */
export interface GSplatBindingRuntime {
  resolveParameter(name: string, data: unknown): GSplatParameter
  invalidatePlacement(placement: GSplatPlacement, reason: GSplatBindingInvalidationReason): void
  stagePlacementReplacement(
    previous: GSplatPlacement,
    next: GSplatPlacement
  ): GSplatBindingTransaction
  stageWorkBufferModifier(
    placement: GSplatPlacement,
    modifier: CompiledGSplatWorkBufferModifier | null
  ): GSplatBindingTransaction
}

export interface GSplatHandleBehavior {
  /** 读取 System-wide requested/resolved sorting 状态；Handle 仅透传只读快照。 */
  getSortingMode?(): GaussianSplatHandle['sortingMode']
  getResolvedSortingMode?(): GaussianSplatHandle['resolvedSortingMode']
  getLodStats?(): LODStats
  getEnvironmentConfigured?(): boolean
  getEnvironmentVisible?(): boolean
  setEnvironmentVisible?(visible: boolean): boolean
  setScalarFilter?: GaussianSplatHandle['setScalarFilter']
  clearScalarFilter?: GaussianSplatHandle['clearScalarFilter']
  getScalarHistogram?: GaussianSplatHandle['getScalarHistogram']
  setCentersDisplayMode?: GaussianSplatHandle['setCentersDisplayMode']
  setCentersStyle?: GaussianSplatHandle['setCentersStyle']
  setBoxHelperVisible?: GaussianSplatHandle['setBoxHelperVisible']
  setColorSpaceConfig?: GaussianSplatHandle['setColorSpaceConfig']
  setColorAdjustment?: GaussianSplatHandle['setColorAdjustment']
  getColorAdjustment?: GaussianSplatHandle['getColorAdjustment']
  resetColorAdjustment?: GaussianSplatHandle['resetColorAdjustment']
  setShaderEffect?: GaussianSplatHandle['setShaderEffect']
  getShaderEffect?: () => GSplatShaderEffectLike | null
  clearShaderEffect?: GaussianSplatHandle['clearShaderEffect']
  restartShaderEffect?: GaussianSplatHandle['restartShaderEffect']
  setShaderEffectUniform?: GaussianSplatHandle['setShaderEffectUniform']
  setSceneOrigin?: GaussianSplatHandle['setSceneOrigin']
  dispose?(): void
}

export interface GSplatHandleBindingState {
  readonly name: string
  readonly visible: boolean
  readonly enabled: boolean
  readonly sceneAttached: boolean
  readonly mounted: boolean
  readonly layerIds: readonly number[]
  readonly castShadows: boolean
  readonly lodBaseDistance: number
  readonly lodMultiplier: number
  readonly lodRangeMin: number
  readonly lodRangeMax: number
  readonly customAabb: THREE.Box3 | null
  readonly parameters: ReadonlyMap<string, unknown>
  readonly workBufferModifier: GSplatWorkBufferModifier | null
  readonly workBufferUpdateMode: GSplatWorkBufferUpdateMode
  /** 可选的稳定 RTE 场景原点；按 world 坐标保存，不暴露内部 node 引用。 */
  readonly sceneOrigin?: THREE.Vector3 | null
}

/** PlayCanvas GSplatComponent 对应的 per-placement LOD 属性。 */
export interface GSplatAssetLodConfiguration {
  readonly lodBaseDistance?: number
  readonly lodMultiplier?: number
  readonly lodRangeMin?: number
  readonly lodRangeMax?: number
}

export interface GSplatHandleBindingCreateInput {
  readonly id: AssetId
  readonly request: Readonly<GaussianSplatLoadRequest>
  readonly resource: GSplatResourceLease
  readonly generation: number
  readonly source?: GSplatBindingResourceSource
  readonly state?: GSplatHandleBindingState
}

/** System 依赖的 binding 窄协议。binding 独占 resource lease。 */
export interface IGSplatHandleBinding extends Disposable {
  readonly handle: GaussianSplatHandle
  readonly resource: GSplatResourceLease
  readonly generation: number
  readonly mounted: boolean
  setParameter?(name: string, data: unknown): void
  setParameters?(parameters: ReadonlyMap<string, unknown>): void
  getShaderEffect?(): GSplatShaderEffectLike | null
  getParameter?(name: string): unknown
  deleteParameter?(name: string): void
  setWorkBufferModifier?(modifier: GSplatWorkBufferModifier | null): void
  readonly workBufferUpdateMode?: GSplatWorkBufferUpdateMode
  getInstanceTexture?(name: string): GSplatTextureLeaf | null
  markStreamsWritten?(streamNames: readonly string[]): void
  mount(): void
  unmount(): void
  setVisibility(visible: boolean): void
  setEnabled(enabled: boolean): void
  setLodConfiguration(config: Readonly<GSplatAssetLodConfiguration>): void
  snapshotState(): GSplatHandleBindingState
  beginResourceReplacement(): number
  /** 无论成功、失败或 stale，调用后 input.resource 的所有权都已被 binding 消费。 */
  replaceResource(input: {
    readonly token: number
    readonly resource: GSplatResourceLease
    readonly source: GSplatBindingResourceSource
    readonly url?: string
    readonly name?: string
  }): boolean
}

export interface IGSplatHandleBindingFactory {
  create(input: GSplatHandleBindingCreateInput): IGSplatHandleBinding
}

export interface GSplatHandleBindingOptions extends GSplatHandleBindingCreateInput {
  readonly adapter: GSplatGraphicsAdapter
  readonly composition: LayerComposition<GSplatPlacement>
  readonly runtime: GSplatBindingRuntime
  readonly behavior?: GSplatHandleBehavior
  readonly node?: THREE.Object3D
  readonly pickerId?: number
  readonly onDispose?: (binding: GSplatHandleBinding) => void
}

function noopTransaction(): GSplatBindingTransaction {
  return { commit: () => undefined, rollback: () => undefined }
}

function cloneModifier(modifier: GSplatWorkBufferModifier | null): GSplatWorkBufferModifier | null {
  return modifier ? Object.freeze({ ...modifier }) : null
}

function normalizeLayerIds(layerIds: readonly number[]): readonly number[] {
  const unique = new Set<number>()
  for (const id of layerIds) {
    if (!Number.isSafeInteger(id) || id < 0) throw new Error(`非法 GSplat layer id: ${id}`)
    unique.add(id)
  }
  return Object.freeze([...unique])
}

class GaussianSplatHandleFacade implements GaussianSplatHandle {
  readonly kind = 'gaussian-splat' as const

  constructor(private readonly binding: GSplatHandleBinding) {}

  get id(): AssetId {
    return this.binding.id
  }

  get url(): string {
    return this.binding.url
  }

  get name(): string {
    return this.binding.name
  }

  get sortingMode(): GaussianSplatHandle['sortingMode'] {
    return this.binding.behavior.getSortingMode?.() ?? 'auto'
  }

  get resolvedSortingMode(): GaussianSplatHandle['resolvedSortingMode'] {
    return this.binding.behavior.getResolvedSortingMode?.() ?? 'cpu'
  }

  set name(value: string) {
    this.binding.name = value
  }

  get visible(): boolean {
    return this.binding.visible
  }

  set visible(value: boolean) {
    this.binding.setVisibility(value)
  }

  get boundingBox(): THREE.Box3 | null {
    return this.binding.boundingBox
  }

  set boundingBox(value: THREE.Box3 | null) {
    this.binding.customAabb = value
  }

  get object3D(): THREE.Object3D | null {
    return this.binding.node
  }

  get numSplats(): number {
    return this.binding.resource.numSplats
  }

  get lodStats(): LODStats {
    return this.binding.behavior.getLodStats?.() ?? EMPTY_LOD_STATS
  }

  get environmentConfigured(): boolean {
    return this.binding.behavior.getEnvironmentConfigured?.() ?? false
  }

  get environmentVisible(): boolean {
    return this.binding.behavior.getEnvironmentVisible?.() ?? false
  }

  setEnvironmentVisible(visible: boolean): boolean {
    return this.binding.behavior.setEnvironmentVisible?.(visible) ?? false
  }

  setScalarFilter(...args: Parameters<GaussianSplatHandle['setScalarFilter']>): boolean {
    return this.binding.requireBehavior('setScalarFilter')(...args)
  }

  clearScalarFilter(): boolean {
    return this.binding.requireBehavior('clearScalarFilter')()
  }

  getScalarHistogram(
    ...args: Parameters<GaussianSplatHandle['getScalarHistogram']>
  ): ReturnType<GaussianSplatHandle['getScalarHistogram']> {
    return this.binding.requireBehavior('getScalarHistogram')(...args)
  }

  setCentersDisplayMode(
    ...args: Parameters<GaussianSplatHandle['setCentersDisplayMode']>
  ): boolean {
    return this.binding.requireBehavior('setCentersDisplayMode')(...args)
  }

  setCentersStyle(...args: Parameters<GaussianSplatHandle['setCentersStyle']>): boolean {
    return this.binding.requireBehavior('setCentersStyle')(...args)
  }

  setBoxHelperVisible(...args: Parameters<GaussianSplatHandle['setBoxHelperVisible']>): void {
    this.binding.setBoxHelperVisible(...args)
  }

  setColorSpaceConfig(...args: Parameters<GaussianSplatHandle['setColorSpaceConfig']>): void {
    this.binding.requireBehavior('setColorSpaceConfig')(...args)
  }

  setColorAdjustment(...args: Parameters<GaussianSplatHandle['setColorAdjustment']>): boolean {
    return this.binding.requireBehavior('setColorAdjustment')(...args)
  }

  getColorAdjustment(): ReturnType<GaussianSplatHandle['getColorAdjustment']> {
    return this.binding.requireBehavior('getColorAdjustment')()
  }

  resetColorAdjustment(): boolean {
    return this.binding.requireBehavior('resetColorAdjustment')()
  }

  setShaderEffect(...args: Parameters<GaussianSplatHandle['setShaderEffect']>): boolean {
    return this.binding.requireBehavior('setShaderEffect')(...args)
  }

  clearShaderEffect(): boolean {
    return this.binding.requireBehavior('clearShaderEffect')()
  }

  restartShaderEffect(): boolean {
    return this.binding.requireBehavior('restartShaderEffect')()
  }

  setShaderEffectUniform(
    ...args: Parameters<GaussianSplatHandle['setShaderEffectUniform']>
  ): boolean {
    return this.binding.requireBehavior('setShaderEffectUniform')(...args)
  }

  setWorkBufferModifier(modifier: GSplatWorkBufferModifier | null): boolean {
    this.binding.setWorkBufferModifier(modifier)
    return true
  }

  setWorkBufferUpdateMode(mode: GSplatWorkBufferUpdateMode): boolean {
    this.binding.workBufferUpdateMode = mode
    return true
  }

  setSceneOrigin(...args: Parameters<GaussianSplatHandle['setSceneOrigin']>): boolean {
    return this.binding.setSceneOrigin(...args)
  }
}

/** PlayCanvas GSplatComponent 在本地非 ECS 架构中的统一对象模型。 */
export class GSplatHandleBinding implements IGSplatHandleBinding {
  readonly id: AssetId
  readonly pickerId: number
  readonly node: THREE.Object3D
  readonly handle!: GaussianSplatHandle
  readonly behavior: GSplatHandleBehavior

  private readonly adapter: GSplatGraphicsAdapter
  private readonly runtime: GSplatBindingRuntime
  private compositionValue: LayerComposition<GSplatPlacement>
  private compositionLease: Disposable | null = null
  private resourceValue: GSplatResourceLease
  private placementValue!: GSplatPlacement
  private sourceValue: GSplatBindingResourceSource
  private generationValue: number
  private nextGeneration: number
  private urlValue: string
  private nameValue: string
  private visibleValue = true
  private enabledValue = true
  private sceneAttachedValue = true
  private mountRequested = false
  private layerIdsValue: readonly number[] = Object.freeze([DEFAULT_LAYER_ID])
  private castShadowsValue = false
  private lodBaseDistanceValue = 5
  private lodMultiplierValue = 3
  private lodRangeMinValue = 0
  private lodRangeMaxValue = 99
  private customAabbValue: THREE.Box3 | null = null
  private readonly parameters = new Map<string, GSplatParameter>()
  private modifierSource: GSplatWorkBufferModifier | null = null
  private compiledModifier: CompiledGSplatWorkBufferModifier | null = null
  private modifierBoundsDiagnosticEmitted = false
  private workBufferUpdateValue: GSplatWorkBufferUpdateMode = GSPLAT_WORKBUFFER_UPDATE_AUTO
  private attachedMain = new Set<Layer<GSplatPlacement>>()
  private attachedShadow = new Set<Layer<GSplatPlacement>>()
  private disposed = false
  private sceneOriginValue: THREE.Vector3 | null = null
  private readonly onDispose?: (binding: GSplatHandleBinding) => void

  constructor(options: GSplatHandleBindingOptions) {
    this.id = options.id
    this.adapter = options.adapter
    this.runtime = options.runtime
    this.behavior = options.behavior ?? {}
    this.compositionValue = options.composition
    this.resourceValue = options.resource
    this.sourceValue = options.source ?? { kind: 'asset', assetId: options.id }
    this.onDispose = options.onDispose
    this.generationValue = options.generation
    this.nextGeneration = options.generation
    this.urlValue = options.request.url
    this.nameValue = options.state?.name ?? options.request.name ?? options.request.url
    this.pickerId = options.pickerId ?? nextPickerId++
    this.node = options.node ?? options.resource.object3D ?? new THREE.Object3D()

    const state = options.state
    if (state) this.applyInitialState(state)
    this.assertShadowSupported(this.castShadowsValue)

    let placement: GSplatPlacement | null = null
    try {
      placement = this.createPlacement(options.resource)
      this.placementValue = placement
      this.handle = new GaussianSplatHandleFacade(this)
      this.compositionLease = this.subscribeComposition(this.compositionValue)
      if (this.shouldAttach()) this.transitionMembership(this.compositionValue, this.layerIdsValue)
    } catch (error) {
      this.compositionLease?.dispose()
      placement?.destroy()
      throw error
    }
  }

  get resource(): GSplatResourceLease {
    return this.resourceValue
  }

  get placement(): GSplatPlacement {
    return this.placementValue
  }

  get generation(): number {
    return this.generationValue
  }

  get source(): GSplatBindingResourceSource {
    return this.sourceValue
  }

  get mounted(): boolean {
    return this.mountRequested
  }

  /**
   * 设置 Unified Placement 的场景原点。
   *
   * 兼容层可通过 behavior 提供额外的系统级处理；Unified 默认路径则把 immutable
   * snapshot 写入 Placement node，并定向使该 Placement 失效。这样在下一个 World
   * transaction 中，绘制、排序、bounds 与 picking 都会看到同一个 RTE 标记。
   */
  setSceneOrigin(origin: THREE.Vector3 | null): boolean {
    this.assertActive()
    if (this.behavior.setSceneOrigin) return this.behavior.setSceneOrigin(origin)
    if (origin !== null && !(origin instanceof THREE.Vector3)) {
      throw new Error('GSplat sceneOrigin 必须为 THREE.Vector3 或 null')
    }
    this.sceneOriginValue = origin?.clone() ?? null
    const node = this.node as THREE.Object3D & {
      __sceneOriginOverride?: THREE.Vector3
    }
    if (this.sceneOriginValue) node.__sceneOriginOverride = this.sceneOriginValue.clone()
    else node.__sceneOriginOverride = undefined
    this.runtime.invalidatePlacement(this.placementValue, 'parameter')
    return true
  }

  get url(): string {
    return this.urlValue
  }

  get name(): string {
    return this.nameValue
  }

  set name(value: string) {
    this.assertActive()
    this.nameValue = value
  }

  get visible(): boolean {
    return this.visibleValue
  }

  get enabled(): boolean {
    return this.enabledValue
  }

  set enabled(value: boolean) {
    this.setAttachmentFlag('enabled', value)
  }

  get sceneAttached(): boolean {
    return this.sceneAttachedValue
  }

  set sceneAttached(value: boolean) {
    this.setAttachmentFlag('sceneAttached', value)
  }

  get layerIds(): readonly number[] {
    return this.layerIdsValue
  }

  set layerIds(value: readonly number[]) {
    this.assertActive()
    const normalized = normalizeLayerIds(value)
    this.transitionMembership(this.compositionValue, normalized)
    this.layerIdsValue = normalized
    this.invalidate('membership')
  }

  get castShadows(): boolean {
    return this.castShadowsValue
  }

  set castShadows(value: boolean) {
    this.assertActive()
    this.assertShadowSupported(value)
    if (value === this.castShadowsValue) return
    this.transitionMembership(this.compositionValue, this.layerIdsValue, undefined, value)
    this.castShadowsValue = value
    this.invalidate('membership')
  }

  get lodBaseDistance(): number {
    return this.lodBaseDistanceValue
  }

  set lodBaseDistance(value: number) {
    this.assertActive()
    const next = Math.max(0.1, value)
    this.lodBaseDistanceValue = next
    this.placementValue.lodBaseDistance = next
  }

  get lodMultiplier(): number {
    return this.lodMultiplierValue
  }

  set lodMultiplier(value: number) {
    this.assertActive()
    const next = Math.max(1.2, value)
    this.lodMultiplierValue = next
    this.placementValue.lodMultiplier = next
  }

  get lodRangeMin(): number {
    return this.lodRangeMinValue
  }

  set lodRangeMin(value: number) {
    this.assertActive()
    this.lodRangeMinValue = value
    this.placementValue.lodRangeMin = value
  }

  get lodRangeMax(): number {
    return this.lodRangeMaxValue
  }

  set lodRangeMax(value: number) {
    this.assertActive()
    this.lodRangeMaxValue = value
    this.placementValue.lodRangeMax = value
  }

  setLodConfiguration(config: Readonly<GSplatAssetLodConfiguration>): void {
    this.assertActive()
    const baseDistance = config.lodBaseDistance ?? this.lodBaseDistanceValue
    const multiplier = config.lodMultiplier ?? this.lodMultiplierValue
    const rangeMin = config.lodRangeMin ?? this.lodRangeMinValue
    const rangeMax = config.lodRangeMax ?? this.lodRangeMaxValue
    if (!Number.isFinite(baseDistance) || baseDistance <= 0) {
      throw new Error(`非法 GSplat lodBaseDistance: ${baseDistance}`)
    }
    if (!Number.isFinite(multiplier) || multiplier <= 1) {
      throw new Error(`非法 GSplat lodMultiplier: ${multiplier}`)
    }
    if (!Number.isSafeInteger(rangeMin) || rangeMin < 0) {
      throw new Error(`非法 GSplat lodRangeMin: ${rangeMin}`)
    }
    if (!Number.isSafeInteger(rangeMax) || rangeMax < rangeMin) {
      throw new Error(`非法 GSplat lodRangeMax: ${rangeMax}`)
    }

    this.lodBaseDistance = baseDistance
    this.lodMultiplier = multiplier
    this.lodRangeMin = rangeMin
    this.lodRangeMax = rangeMax
  }

  get customAabb(): THREE.Box3 | null {
    return this.customAabbValue?.clone() ?? null
  }

  set customAabb(value: THREE.Box3 | null) {
    this.assertActive()
    const next = value?.clone() ?? null
    this.placementValue.aabb = next
    this.customAabbValue = next
    this.invalidate('membership')
  }

  get boundingBox(): THREE.Box3 | null {
    try {
      return this.placementValue.aabb
    } catch {
      return this.resourceValue.boundingBox
    }
  }

  get workBufferUpdateMode(): GSplatWorkBufferUpdateMode {
    return this.workBufferUpdateValue
  }

  set workBufferUpdateMode(value: GSplatWorkBufferUpdateMode) {
    this.assertActive()
    if (
      value !== GSPLAT_WORKBUFFER_UPDATE_AUTO &&
      value !== GSPLAT_WORKBUFFER_UPDATE_ONCE &&
      value !== GSPLAT_WORKBUFFER_UPDATE_ALWAYS
    ) {
      throw new Error(`非法 GSplat WorkBuffer update mode: ${value}`)
    }
    this.placementValue.workBufferUpdate = value
    this.workBufferUpdateValue =
      value === GSPLAT_WORKBUFFER_UPDATE_ONCE ? GSPLAT_WORKBUFFER_UPDATE_AUTO : value
    this.invalidate('work-buffer-update')
  }

  mount(): void {
    this.assertActive()
    if (this.mountRequested) return
    this.transitionMembership(this.compositionValue, this.layerIdsValue, true)
    this.mountRequested = true
    this.invalidate('membership')
  }

  unmount(): void {
    if (this.disposed || !this.mountRequested) return
    this.transitionMembership(this.compositionValue, this.layerIdsValue, false)
    this.mountRequested = false
    this.invalidate('membership')
  }

  setVisibility(visible: boolean): void {
    this.assertActive()
    if (this.visibleValue === visible && this.node.visible === visible) return
    this.node.visible = visible
    this.visibleValue = visible
    this.invalidate('visibility')
  }

  setBoxHelperVisible(visible: boolean): void {
    this.assertActive()
    this.behavior.setBoxHelperVisible?.(visible)
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled
  }

  setLayerComposition(composition: LayerComposition<GSplatPlacement>): void {
    this.assertActive()
    if (composition === this.compositionValue) return
    const nextLease = this.subscribeComposition(composition)
    const oldLease = this.compositionLease
    try {
      this.transitionMembership(composition, this.layerIdsValue)
      this.compositionValue = composition
      this.compositionLease = nextLease
      oldLease?.dispose()
      this.invalidate('membership')
    } catch (error) {
      nextLease.dispose()
      this.removePlacementFromComposition(composition, this.placementValue)
      throw error
    }
  }

  setParameter(name: string, data: unknown): void {
    this.assertActive()
    const parameter = this.runtime.resolveParameter(name, data)
    this.parameters.set(name, parameter)
    this.placementValue.markDirty()
    this.invalidate('parameter')
  }

  setParameters(parameters: ReadonlyMap<string, unknown>): void {
    this.assertActive()
    if (parameters.size === 0) return
    const resolved = new Map<string, GSplatParameter>()
    for (const [name, data] of parameters) {
      resolved.set(name, this.runtime.resolveParameter(name, data))
    }
    for (const [name, parameter] of resolved) this.parameters.set(name, parameter)
    this.placementValue.markDirty()
    this.invalidate('parameter')
  }

  getShaderEffect(): GSplatShaderEffectLike | null {
    this.assertActive()
    return this.behavior.getShaderEffect?.() ?? null
  }

  getParameter(name: string): unknown {
    return this.parameters.get(name)?.data
  }

  deleteParameter(name: string): void {
    this.assertActive()
    if (!this.parameters.delete(name)) return
    this.placementValue.markDirty()
    this.invalidate('parameter')
  }

  getInstanceTexture(name: string): GSplatTextureLeaf | null {
    this.assertActive()
    return this.placementValue.getInstanceTexture(name) ?? null
  }

  markStreamsWritten(streamNames: readonly string[]): void {
    this.assertActive()
    if (!this.resourceValue.markStreamsWritten) {
      throw new Error('当前 GSplat Resource 不支持 processor content revision')
    }
    const resourceStreams = streamNames.filter(
      (name) => this.resourceValue.format?.getStream(name)?.storage !== 'instance'
    )
    if (resourceStreams.length > 0) this.resourceValue.markStreamsWritten(resourceStreams)
    this.placementValue.markDirty()
    this.invalidate('processor')
  }

  setWorkBufferModifier(modifier: GSplatWorkBufferModifier | null): void {
    this.assertActive()
    const source = cloneModifier(modifier)
    const compiled = compileGSplatWorkBufferModifierForLanguage(source, this.adapter.language)
    const transaction = this.runtime.stageWorkBufferModifier(this.placementValue, compiled)
    const previous = this.placementValue.workBufferModifier
    try {
      transaction.commit()
      this.placementValue.workBufferModifier = compiled
      this.modifierSource = source
      this.compiledModifier = compiled
      if (compiled && !this.customAabbValue && !this.modifierBoundsDiagnosticEmitted) {
        this.modifierBoundsDiagnosticEmitted = true
        diagnosticLog.warn(
          `GSplat WorkBuffer modifier 可能扩大位移范围，但当前 Placement 未设置 customAabb；` +
            'CPU sort、LOD coarse cull 与 CPU pick 仍使用 source-data bounds'
        )
      }
    } catch (error) {
      this.placementValue.workBufferModifier = previous
      transaction.rollback()
      throw error
    }
    this.runtime.invalidatePlacement(this.placementValue, 'work-buffer-update')
  }

  /** async asset/direct Resource 替换共享的 generation token。 */
  beginResourceReplacement(): number {
    this.assertActive()
    return ++this.nextGeneration
  }

  replaceResource(input: {
    readonly token: number
    readonly resource: GSplatResourceLease
    readonly source: GSplatBindingResourceSource
    readonly url?: string
    readonly name?: string
  }): boolean {
    if (this.disposed || input.token !== this.nextGeneration) {
      input.resource.dispose()
      return false
    }

    let nextPlacement: GSplatPlacement | null = null
    let transaction: GSplatBindingTransaction = noopTransaction()
    const stagedMain = new Set<Layer<GSplatPlacement>>()
    const stagedShadow = new Set<Layer<GSplatPlacement>>()
    try {
      nextPlacement = this.createPlacement(input.resource)
      transaction = this.runtime.stagePlacementReplacement(this.placementValue, nextPlacement)
      if (this.shouldAttach()) {
        this.addPlacementToResolvedLayers(
          nextPlacement,
          this.compositionValue,
          this.layerIdsValue,
          this.castShadowsValue,
          stagedMain,
          stagedShadow
        )
      }
      transaction.commit()
    } catch (error) {
      transaction.rollback()
      this.removePlacementFromSets(nextPlacement, stagedMain, stagedShadow)
      nextPlacement?.destroy()
      input.resource.dispose()
      throw error
    }

    const previousPlacement = this.placementValue
    const previousResource = this.resourceValue
    this.removePlacementFromSets(previousPlacement, this.attachedMain, this.attachedShadow)
    this.attachedMain = stagedMain
    this.attachedShadow = stagedShadow
    this.placementValue = nextPlacement
    this.resourceValue = input.resource
    this.sourceValue = input.source
    this.generationValue = input.token
    if (input.url !== undefined) this.urlValue = input.url
    if (input.name !== undefined) this.nameValue = input.name
    previousPlacement.destroy()
    previousResource.dispose()
    this.invalidate('resource-replaced')
    return true
  }

  snapshotState(): GSplatHandleBindingState {
    return Object.freeze({
      name: this.nameValue,
      visible: this.visibleValue,
      enabled: this.enabledValue,
      sceneAttached: this.sceneAttachedValue,
      mounted: this.mountRequested,
      layerIds: Object.freeze([...this.layerIdsValue]),
      castShadows: this.castShadowsValue,
      lodBaseDistance: this.lodBaseDistanceValue,
      lodMultiplier: this.lodMultiplierValue,
      lodRangeMin: this.lodRangeMinValue,
      lodRangeMax: this.lodRangeMaxValue,
      customAabb: this.customAabbValue?.clone() ?? null,
      parameters: new Map(
        Array.from(this.parameters, ([name, parameter]) => [name, parameter.data] as const)
      ),
      workBufferModifier: cloneModifier(this.modifierSource),
      workBufferUpdateMode: this.workBufferUpdateValue,
      sceneOrigin: this.sceneOriginValue?.clone() ?? null,
    })
  }

  /** 未进入公共 Handle 的上游 legacy API 必须明确失败，不能形成 WebGPU 旁路。 */
  rejectLegacyCompatibility(api: string): never {
    throw new UnsupportedGraphicsCapabilityError(
      'gsplat-legacy-non-unified',
      this.adapter.backend,
      `${api} 不属于 Unified GSplatHandleBinding API`
    )
  }

  requireBehavior<K extends keyof GSplatHandleBehavior>(
    operation: K
  ): NonNullable<GSplatHandleBehavior[K]> {
    this.assertActive()
    const implementation = this.behavior[operation]
    if (!implementation) {
      const capability = this.adapter.backend === 'webgpu' ? getWebGpuCapability(operation) : null
      throw new UnsupportedGraphicsCapabilityError(
        capability ?? `gsplat-handle:${String(operation)}`,
        this.adapter.backend,
        capability ? `${String(operation)} 尚未在 WebGPU Unified 中激活` : undefined
      )
    }
    return implementation as NonNullable<GSplatHandleBehavior[K]>
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.generationValue = ++this.nextGeneration
    let firstError: unknown
    const step = (operation: () => void) => {
      try {
        operation()
      } catch (error) {
        firstError ??= error
      }
    }
    step(() =>
      this.removePlacementFromSets(this.placementValue, this.attachedMain, this.attachedShadow)
    )
    step(() => this.compositionLease?.dispose())
    this.compositionLease = null
    step(() => this.behavior.dispose?.())
    step(() => this.placementValue.destroy())
    step(() => this.resourceValue.dispose())
    step(() => this.node.removeFromParent())
    step(() => this.onDispose?.(this))
    if (firstError) throw firstError
  }

  private applyInitialState(state: GSplatHandleBindingState): void {
    this.visibleValue = state.visible
    this.enabledValue = state.enabled
    this.sceneAttachedValue = state.sceneAttached
    this.mountRequested = state.mounted
    this.layerIdsValue = normalizeLayerIds(state.layerIds)
    this.castShadowsValue = state.castShadows
    this.lodBaseDistanceValue = Math.max(0.1, state.lodBaseDistance)
    this.lodMultiplierValue = Math.max(1.2, state.lodMultiplier)
    this.lodRangeMinValue = state.lodRangeMin
    this.lodRangeMaxValue = state.lodRangeMax
    this.customAabbValue = state.customAabb?.clone() ?? null
    this.sceneOriginValue = state.sceneOrigin?.clone() ?? null
    for (const [name, data] of state.parameters) {
      this.parameters.set(name, this.runtime.resolveParameter(name, data))
    }
    this.modifierSource = cloneModifier(state.workBufferModifier)
    this.compiledModifier = compileGSplatWorkBufferModifierForLanguage(
      this.modifierSource,
      this.adapter.language
    )
    this.workBufferUpdateValue =
      state.workBufferUpdateMode === GSPLAT_WORKBUFFER_UPDATE_ONCE
        ? GSPLAT_WORKBUFFER_UPDATE_AUTO
        : state.workBufferUpdateMode
    if (this.sceneOriginValue) {
      ;(
        this.node as THREE.Object3D & { __sceneOriginOverride?: THREE.Vector3 }
      ).__sceneOriginOverride = this.sceneOriginValue.clone()
    }
  }

  private createPlacement(resource: GSplatResourceLease): GSplatPlacement {
    const identity = resource.resourceIdentity
    const placementResource: GSplatPlacementResource =
      identity && typeof identity === 'object' && 'aabb' in identity && 'acquire' in identity
        ? (identity as GSplatPlacementResource)
        : {
            resourceIdentity: identity,
            graphicsAdapter: resource.graphicsAdapter,
            aabb: resource.aabb ?? resource.boundingBox,
            format: resource.format,
            streams: resource.streams,
            acquire: () => resource.acquire(),
          }
    const placement = new GSplatPlacement(
      this.adapter,
      placementResource,
      this.node,
      0,
      this.parameters,
      null,
      this.pickerId,
      this.id
    )
    try {
      placement.lodBaseDistance = this.lodBaseDistanceValue
      placement.lodMultiplier = this.lodMultiplierValue
      placement.lodRangeMin = this.lodRangeMinValue
      placement.lodRangeMax = this.lodRangeMaxValue
      placement.aabb = this.customAabbValue
      placement.workBufferUpdate = this.workBufferUpdateValue
      placement.workBufferModifier = this.compiledModifier
      return placement
    } catch (error) {
      placement.destroy()
      throw error
    }
  }

  private setAttachmentFlag(flag: 'enabled' | 'sceneAttached', value: boolean): void {
    this.assertActive()
    const current = flag === 'enabled' ? this.enabledValue : this.sceneAttachedValue
    if (current === value) return
    const enabled = flag === 'enabled' ? value : this.enabledValue
    const sceneAttached = flag === 'sceneAttached' ? value : this.sceneAttachedValue
    const shouldAttach = this.mountRequested && enabled && sceneAttached
    this.transitionMembership(this.compositionValue, this.layerIdsValue, shouldAttach)
    if (flag === 'enabled') this.enabledValue = value
    else this.sceneAttachedValue = value
    this.invalidate('membership')
  }

  private shouldAttach(): boolean {
    return this.mountRequested && this.enabledValue && this.sceneAttachedValue
  }

  private transitionMembership(
    composition: LayerComposition<GSplatPlacement>,
    layerIds: readonly number[],
    shouldAttach = this.shouldAttach(),
    castShadows = this.castShadowsValue
  ): void {
    const nextMain = new Set<Layer<GSplatPlacement>>()
    const nextShadow = new Set<Layer<GSplatPlacement>>()
    const addedMain = new Set<Layer<GSplatPlacement>>()
    const addedShadow = new Set<Layer<GSplatPlacement>>()
    if (shouldAttach) {
      this.addPlacementToResolvedLayers(
        this.placementValue,
        composition,
        layerIds,
        castShadows,
        nextMain,
        nextShadow,
        addedMain,
        addedShadow
      )
    }

    try {
      for (const layer of this.attachedShadow) {
        if (!nextShadow.has(layer)) layer.removeGSplatShadowCaster(this.placementValue)
      }
      for (const layer of this.attachedMain) {
        if (!nextMain.has(layer)) layer.removeGSplatPlacement(this.placementValue)
      }
    } catch (error) {
      this.removePlacementFromSets(this.placementValue, addedMain, addedShadow)
      throw error
    }
    this.attachedMain = nextMain
    this.attachedShadow = nextShadow
  }

  private addPlacementToResolvedLayers(
    placement: GSplatPlacement,
    composition: LayerComposition<GSplatPlacement>,
    layerIds: readonly number[],
    castShadows: boolean,
    main: Set<Layer<GSplatPlacement>>,
    shadow: Set<Layer<GSplatPlacement>>,
    addedMain = new Set<Layer<GSplatPlacement>>(),
    addedShadow = new Set<Layer<GSplatPlacement>>()
  ): void {
    try {
      for (const layerId of layerIds) {
        const layer = composition.getLayerById(layerId)
        if (!layer) continue
        const hadMain = layer.gsplatPlacementsSet.has(placement)
        layer.addGSplatPlacement(placement)
        main.add(layer)
        if (!hadMain) addedMain.add(layer)
        if (castShadows) {
          const hadShadow = layer.gsplatShadowCastersSet.has(placement)
          layer.addGSplatShadowCaster(placement)
          shadow.add(layer)
          if (!hadShadow) addedShadow.add(layer)
        }
      }
    } catch (error) {
      this.removePlacementFromSets(placement, addedMain, addedShadow)
      throw error
    }
  }

  private removePlacementFromSets(
    placement: GSplatPlacement | null,
    main: ReadonlySet<Layer<GSplatPlacement>>,
    shadow: ReadonlySet<Layer<GSplatPlacement>>
  ): void {
    if (!placement) return
    for (const layer of shadow) layer.removeGSplatShadowCaster(placement)
    for (const layer of main) layer.removeGSplatPlacement(placement)
  }

  private removePlacementFromComposition(
    composition: LayerComposition<GSplatPlacement>,
    placement: GSplatPlacement
  ): void {
    for (const layer of composition.layerList) {
      layer.removeGSplatShadowCaster(placement)
      layer.removeGSplatPlacement(placement)
      this.attachedShadow.delete(layer)
      this.attachedMain.delete(layer)
    }
  }

  private subscribeComposition(composition: LayerComposition<GSplatPlacement>): Disposable {
    return composition.subscribeLayerChanges((change) => this.onLayerCompositionChange(change))
  }

  private onLayerCompositionChange(change: LayerCompositionChange<GSplatPlacement, object>): void {
    if (this.disposed || !this.layerIdsValue.includes(change.layer.id)) return
    if (change.type === 'removed') {
      change.layer.removeGSplatShadowCaster(this.placementValue)
      change.layer.removeGSplatPlacement(this.placementValue)
      this.attachedShadow.delete(change.layer)
      this.attachedMain.delete(change.layer)
    } else if (this.shouldAttach()) {
      change.layer.addGSplatPlacement(this.placementValue)
      this.attachedMain.add(change.layer)
      if (this.castShadowsValue) {
        change.layer.addGSplatShadowCaster(this.placementValue)
        this.attachedShadow.add(change.layer)
      }
    }
    this.invalidate('membership')
  }

  private assertShadowSupported(castShadows: boolean): void {
    if (!castShadows || this.adapter.backend !== 'webgpu') return
    throw new UnsupportedGraphicsCapabilityError(
      'webgpu-activation:hybrid-shadow-submission',
      this.adapter.backend
    )
  }

  private invalidate(reason: GSplatBindingInvalidationReason): void {
    this.runtime.invalidatePlacement(this.placementValue, reason)
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('GSplatHandleBinding 已销毁')
  }
}

function getWebGpuCapability(operation: keyof GSplatHandleBehavior): string | null {
  switch (operation) {
    case 'setScalarFilter':
    case 'clearScalarFilter':
      return 'webgpu-activation:gsplat-scalar-filter'
    case 'getScalarHistogram':
      return 'webgpu-activation:gsplat-scalar-histogram'
    case 'setCentersDisplayMode':
    case 'setCentersStyle':
      return 'webgpu-activation:gsplat-centers-display'
    case 'setColorSpaceConfig':
      return 'webgpu-activation:gsplat-color-space'
    default:
      return null
  }
}

export interface GSplatHandleBindingFactoryOptions {
  readonly adapter: GSplatGraphicsAdapter
  readonly composition: LayerComposition<GSplatPlacement>
  readonly runtime: GSplatBindingRuntime
  readonly createBehavior?: (input: GSplatHandleBindingCreateInput) => GSplatHandleBehavior
  readonly createNode?: (input: GSplatHandleBindingCreateInput) => THREE.Object3D
  readonly onDispose?: (binding: GSplatHandleBinding) => void
}

/** GSplatSystem 注入的默认 binding factory；WebGL2/WebGPU 仅替换 adapter。 */
export class GSplatHandleBindingFactory implements IGSplatHandleBindingFactory {
  constructor(private readonly options: GSplatHandleBindingFactoryOptions) {}

  create(input: GSplatHandleBindingCreateInput): GSplatHandleBinding {
    return new GSplatHandleBinding({
      ...input,
      adapter: this.options.adapter,
      composition: this.options.composition,
      runtime: this.options.runtime,
      behavior: this.options.createBehavior?.(input),
      node: this.options.createNode?.(input),
      onDispose: this.options.onDispose,
    })
  }
}
